import * as THREE from 'three';
import gsap from 'gsap';
import SandboxBehavior from './SandboxBehavior.js';
import Space from '../Components/Space.js';
import Card from '../Components/Card.js';

/**
 * Automated fair display behavior.
 *
 * Loads all cards into a compact face-up grid, then continuously animates
 * cards around the table by moving one card next to another along a curved
 * path. Cards stay face-up throughout the presentation.
 */
export default class FairDisplayBehavior extends SandboxBehavior {
    static TABLE_SAFE_WIDTH = 12.6;
    static TABLE_SAFE_HEIGHT = 7.2;
    static MOVE_MIN_DELAY = 0.9;
    static MOVE_MAX_DELAY = 1.8;
    static DOUBLE_MOVE_CHANCE = 0.28;

    constructor(space, interaction, runtimeState = {}) {
        super(space, interaction, runtimeState);
        this.cards = [];
        this.destroyed = false;
        this.delayedCalls = new Set();
        this.tweens = new Set();
    }

    canUseFocusToggle() {
        return false;
    }

    onSetup(cards) {
        this.destroyed = false;
        this.cards = [...cards].sort((a, b) => a.index - b.index);
        this._stopAutomation();
        this._arrangeCardsInGrid(this.cards);
        this._scheduleNextCycle(1.4);
    }

    onDestroy() {
        this.destroyed = true;
        this._stopAutomation();
    }

    onTap() {}
    onDoubleTap() {}
    onPanStart() { return false; }
    onPanMove() {}
    onPanEnd() {}
    onMouseMove() {}
    onScrollWheel() {}
    onKeyDown() {}
    onKeyUp() {}
    onWindowBlur() {}

    async _performCycle() {
        if (this.destroyed) return;

        const moveInParallel = Math.random() < FairDisplayBehavior.DOUBLE_MOVE_CHANCE;

        if (moveInParallel) {
            await this._moveTwoCardsInParallel();
        } else {
            await this._moveRandomCardNextToAnother();
        }
        if (this.destroyed) return;

        this._scheduleNextCycle(this._rand(
            FairDisplayBehavior.MOVE_MIN_DELAY,
            FairDisplayBehavior.MOVE_MAX_DELAY,
        ));
    }

    _scheduleNextCycle(delaySeconds) {
        if (this.destroyed) return;

        const call = gsap.delayedCall(delaySeconds, () => {
            this.delayedCalls.delete(call);
            Promise.resolve(this._performCycle()).catch((error) => {
                console.error('FairDisplayBehavior cycle failed', error);
            });
        });

        this.delayedCalls.add(call);
    }

    _stopAutomation() {
        for (const call of this.delayedCalls) {
            call.kill();
        }
        this.delayedCalls.clear();

        for (const tween of this.tweens) {
            tween.kill();
        }
        this.tweens.clear();
    }

    _arrangeCardsInGrid(cards) {
        const positions = this._buildGridPositions(cards);

        this.space.highlightedCard = null;
        this.space.selectedCard = null;
        this.space.focusedCard = null;
        this.space.highLiftedCard = null;
        this.space.liftedCard = null;

        cards.forEach((card, index) => {
            const targetPosition = positions[index];
            const wasFaceUp = card.isFaceUp;

            gsap.killTweensOf(card.wrapper.position);
            gsap.killTweensOf(card.model.rotation);
            gsap.killTweensOf(card.wrapper.quaternion);

            card.isSelected = false;
            card.isHighlighted = false;
            card.isLifted = false;
            card.isHighLifted = false;
            card.isFocused = false;
            card.isFaceUp = true;
            card.sortOrder = card.index;

            card.wrapper.quaternion.copy(Card.UP_QUATERNION);
            this.space.endCardMotion(card, targetPosition, { lifted: false, resetRotation: true });

            this.space.eventBus.pushRemote('draggedCard', {
                id: card.id,
                x: targetPosition.x,
                y: targetPosition.y,
            });

            if (!wasFaceUp) {
                this.space.eventBus.pushRemote('flippedCardUp', {
                    id: card.id,
                    index: card.sortOrder,
                });
            }
        });

        this.space.emitCardStateChanged();
    }

    _buildGridPositions(cards) {
        const count = cards.length;
        if (count === 0) return [];

        const usableWidth = FairDisplayBehavior.TABLE_SAFE_WIDTH;
        const usableHeight = FairDisplayBehavior.TABLE_SAFE_HEIGHT;
        const cols = Math.max(1, Math.ceil(Math.sqrt(count * (usableWidth / usableHeight))));
        const rows = Math.max(1, Math.ceil(count / cols));
        const maxCardWidth = Math.max(...cards.map((card) => card.width || 1));
        const maxCardHeight = Math.max(...cards.map((card) => card.height || 1.5));

        const desiredGapX = 0.14;
        const desiredGapY = 0.16;
        const availableGapX = cols > 1 ? (usableWidth - cols * maxCardWidth) / (cols - 1) : 0;
        const availableGapY = rows > 1 ? (usableHeight - rows * maxCardHeight) / (rows - 1) : 0;
        const gapX = cols > 1 ? THREE.MathUtils.clamp(availableGapX, 0.03, desiredGapX) : 0;
        const gapY = rows > 1 ? THREE.MathUtils.clamp(availableGapY, 0.04, desiredGapY) : 0;
        const stepX = maxCardWidth + gapX;
        const stepY = maxCardHeight + gapY;
        const totalWidth = cols * maxCardWidth + (cols - 1) * gapX;
        const totalHeight = rows * maxCardHeight + (rows - 1) * gapY;
        const startX = -totalWidth / 2 + maxCardWidth / 2;
        const startY = totalHeight / 2 - maxCardHeight / 2;

        return cards.map((card, index) => {
            const column = index % cols;
            const row = Math.floor(index / cols);

            return new THREE.Vector3(
                startX + column * stepX,
                startY - row * stepY,
                Space.CARD_DOWN_Z,
            );
        });
    }

    async _moveRandomCardNextToAnother(excludedIds = new Set()) {
        const source = this._pickMoveSource(excludedIds);
        if (!source) return false;

        const anchor = this._pickMoveAnchor(source, excludedIds);
        if (!anchor) return false;

        const destination = this._chooseAdjacentPosition(anchor, source);
        await this._animateCurvedMove(source, destination);
        return true;
    }

    async _moveTwoCardsInParallel() {
        const excludedIds = new Set();
        const pairs = [];

        for (let i = 0; i < 2; i++) {
            const source = this._pickMoveSource(excludedIds);
            if (!source) break;

            excludedIds.add(source.id);
            const anchor = this._pickMoveAnchor(source, excludedIds);
            if (!anchor) break;

            excludedIds.add(anchor.id);
            pairs.push({
                card: source,
                destination: this._chooseAdjacentPosition(anchor, source),
            });
        }

        if (pairs.length === 0) return false;
        await Promise.all(pairs.map(({ card, destination }) => this._animateCurvedMove(card, destination)));
        return true;
    }

    _pickMoveSource(excludedIds = new Set()) {
        return this._sample(this.cards.filter((card) => !excludedIds.has(card.id)));
    }

    _pickMoveAnchor(source, excludedIds = new Set()) {
        return this._sample(
            this.cards.filter((card) => card.id !== source.id && !excludedIds.has(card.id)),
        );
    }

    _chooseAdjacentPosition(anchor, card) {
        const horizontalGap = card.width + 0.22;
        const verticalGap = card.height + 0.22;
        const candidates = this._shuffle([
            { x: anchor.position.x + horizontalGap, y: anchor.position.y + this._rand(-0.16, 0.16) },
            { x: anchor.position.x - horizontalGap, y: anchor.position.y + this._rand(-0.16, 0.16) },
            { x: anchor.position.x + this._rand(-0.12, 0.12), y: anchor.position.y + verticalGap },
            { x: anchor.position.x + this._rand(-0.12, 0.12), y: anchor.position.y - verticalGap },
        ]);

        for (const candidate of candidates) {
            if (this._isInsideSafeBounds(candidate.x, candidate.y, card)) {
                return new THREE.Vector3(candidate.x, candidate.y, Space.CARD_DOWN_Z);
            }
        }

        const fallback = candidates[0] || { x: anchor.position.x, y: anchor.position.y };
        return new THREE.Vector3(
            this._clampX(fallback.x, card),
            this._clampY(fallback.y, card),
            Space.CARD_DOWN_Z,
        );
    }

    _isInsideSafeBounds(x, y, card) {
        return x === this._clampX(x, card) && y === this._clampY(y, card);
    }

    _clampX(x, card) {
        const halfWidth = FairDisplayBehavior.TABLE_SAFE_WIDTH / 2;
        const margin = card.width * 0.6;
        return THREE.MathUtils.clamp(x, -halfWidth + margin, halfWidth - margin);
    }

    _clampY(y, card) {
        const halfHeight = FairDisplayBehavior.TABLE_SAFE_HEIGHT / 2;
        const margin = card.height * 0.58;
        return THREE.MathUtils.clamp(y, -halfHeight + margin, halfHeight - margin);
    }

    async _animateCurvedMove(card, targetPosition) {
        if (this.destroyed) return false;

        const from = card.position.clone();
        const to = targetPosition.clone();
        const mid = from.clone().lerp(to, 0.5);
        const direction = new THREE.Vector2(to.x - from.x, to.y - from.y);
        let perpendicular = new THREE.Vector2(-direction.y, direction.x);

        if (perpendicular.lengthSq() < 0.0001) {
            perpendicular = new THREE.Vector2(Math.random() < 0.5 ? -1 : 1, 0);
        }

        perpendicular.normalize();

        const curveStrength = THREE.MathUtils.clamp(direction.length() * 0.28, 0.65, 1.5) * (Math.random() < 0.5 ? -1 : 1);
        const controlPoint = new THREE.Vector3(
            mid.x + perpendicular.x * curveStrength + this._rand(-0.2, 0.2),
            mid.y + perpendicular.y * curveStrength + this._rand(-0.2, 0.2),
            Space.CARD_DOWN_Z,
        );
        const duration = this._rand(0.65, 1.25);
        const liftBase = Space.CARD_LIFTED_REMOTE_Z;
        const liftHeight = this._rand(0.42, 0.8);
        const tiltStrength = 0.09;
        const progress = { value: 0 };

        this.space.reorderCards(card);
        this.space.eventBus.pushRemote('deselectedCard', { id: card.id, index: card.sortOrder });
        this.space.beginCardMotion(card, { lifted: true });

        return new Promise((resolve) => {
            let tween = null;

            tween = gsap.to(progress, {
                value: 1,
                duration,
                ease: 'power2.inOut',
                overwrite: 'auto',
                onUpdate: () => {
                    const t = progress.value;
                    const point = this._quadraticBezier(from, controlPoint, to, t);
                    const tangent = this._quadraticBezierTangent(from, controlPoint, to, t);
                    const arc = Math.sin(Math.PI * t) * liftHeight;
                    const rotationX = THREE.MathUtils.clamp(-tangent.y * tiltStrength, -0.26, 0.26);
                    const rotationY = THREE.MathUtils.clamp(tangent.x * tiltStrength, -0.26, 0.26);
                    const liftedZ = liftBase + arc;

                    this.space.updateCardMotion(
                        card,
                        new THREE.Vector3(point.x, point.y, liftedZ),
                        { rotationX, rotationY }
                    );
                },
                onComplete: () => {
                    this.tweens.delete(tween);
                    this.space.endCardMotion(card, to, { lifted: false, resetRotation: true });

                    this.space.eventBus.pushRemote('draggedCard', {
                        id: card.id,
                        x: to.x,
                        y: to.y,
                    });

                    resolve(true);
                },
            });

            this.tweens.add(tween);
        });
    }

    async _revealRandomCard() {
        const candidates = this.cards.filter((card) => !card.isFaceUp);
        const card = this._sample(candidates);
        if (!card) return false;

        this.space.flipCard(card);
        await this._wait(this._rand(1.4, 2.4));
        if (this.destroyed) return false;

        if (card.isFaceUp) {
            this.space.flipCard(card);
            await this._wait(this._rand(0.45, 0.85));
        }

        return true;
    }

    _quadraticBezier(start, control, end, t) {
        const omt = 1 - t;
        return new THREE.Vector3(
            omt * omt * start.x + 2 * omt * t * control.x + t * t * end.x,
            omt * omt * start.y + 2 * omt * t * control.y + t * t * end.y,
            omt * omt * start.z + 2 * omt * t * control.z + t * t * end.z,
        );
    }

    _quadraticBezierTangent(start, control, end, t) {
        return new THREE.Vector2(
            2 * (1 - t) * (control.x - start.x) + 2 * t * (end.x - control.x),
            2 * (1 - t) * (control.y - start.y) + 2 * t * (end.y - control.y),
        );
    }

    _wait(seconds) {
        return new Promise((resolve) => {
            if (this.destroyed) {
                resolve(false);
                return;
            }

            const call = gsap.delayedCall(seconds, () => {
                this.delayedCalls.delete(call);
                resolve(true);
            });

            this.delayedCalls.add(call);
        });
    }

    _shuffle(items) {
        const result = [...items];
        for (let i = result.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [result[i], result[j]] = [result[j], result[i]];
        }
        return result;
    }

    _sample(items) {
        if (!items.length) return null;
        return items[Math.floor(Math.random() * items.length)];
    }

    _rand(min, max) {
        return THREE.MathUtils.randFloat(min, max);
    }
}
